import React, { useEffect, useMemo, useRef } from 'react';

import type { ShareActionComponentProps, ShareTrackingDataOverride } from '#src/app/shared/hooks/share';

import type { FacebookPostSheetTreatOutcome } from './firedFacebookPostSheet';
import type { FacebookPostSheetTap } from './useFacebookPostSheet';
import type { FacebookPostSheetArm } from './useTreatFacebookPostSheetExperiment';

/**
 * Declared structurally rather than imported: importing `PetitionShareButtonConfig` from
 * `shared/components` would close a module cycle, and `import/no-cycle` is an error.
 */
type InterceptableShareConfig = {
	channel: string;
	ShareAction: (props: ShareActionComponentProps) => React.JSX.Element;
};

type Params = {
	/**
	 * Cancels a pending copy-link redirect (`onShareTapped`, CHANGE-97962). Called synchronously at every
	 * tap, before the treat wait: the pipeline's own call comes after it, and the 2 s copy redirect can fire
	 * during the wait and leave the page. The pipeline's later call does no harm.
	 */
	onTap: () => void;
	treatAtTap: () => Promise<FacebookPostSheetTreatOutcome>;
	reportTap: (treatOutcome: FacebookPostSheetTreatOutcome) => void;
	/** The sheet a treated tap opens, or `null` for the production share. */
	sheetArm: FacebookPostSheetArm | null;
	/** Whether a production tap waits for a treat, and so opens the composer through `shareAtTap`. */
	treatsAtTap: boolean;
	/** The production share that opens the composer inside the tap and holds its events for the treat. */
	shareAtTap: (tap: FacebookPostSheetTap, treatSettled: Promise<unknown>) => Promise<boolean>;
	/**
	 * Copies the post for the sheet's copied pill. Called synchronously in a sheet-path tap, before the
	 * treat wait.
	 */
	copyAtTap: () => Promise<boolean>;
	/** Fires the tap events and opens the sheet. Called after the slide-up's own `onBeforeAction`. */
	onSheetTap: (tap: FacebookPostSheetTap) => void;
};

type TapRouterProps = {
	child: React.ReactElement<{ onClick?: () => void }>;
	/** Injected by the original `ShareAction`, which clones its child with the production share handler. */
	onClick?: () => void;
	decide: (productionShare: (() => void) | undefined) => void;
};

/**
 * Sits between the original `ShareAction` and the real button. The original clones this element with
 * the production share handler as `onClick`; the router passes the real button its own `onClick`, which
 * decides at tap time whether to run that handler or open the sheet. A function-type share cannot be
 * cancelled from `onBeforeAction` (`actions.ts` awaits it, then always shares), so the decision has
 * to sit in front of the handler.
 */
const TapRouter: React.FC<TapRouterProps> = ({ child, onClick, decide }) =>
	React.cloneElement(child, { ...child.props, onClick: () => decide(onClick) });

/**
 * Wraps the Facebook channel's `ShareAction`, wherever it appears in `buttonConfigs`. The grid icon and
 * the channel-aware CTA button resolve their tap through that same config object (see
 * `PetitionShareSlideUp`'s `ctaButtonConfig = buttonConfigs.find(...)`), so one wrapper covers both
 * (plan KTD1).
 *
 * Every tap cancels a pending copy-link redirect, awaits the capped treat, then reports the probe. Then:
 * - production for a tap that treats (`control`, `fake_control`): `shareAtTap` opens the composer inside
 *   the tap, before the treat wait, because iOS Safari blocks a composer that opens after a network
 *   wait. It holds the share events until the treat settles, so they carry the arm's
 *   `experiment_variations`. The original share does not run.
 * - production for a tap that does not treat (an ineligible supporter, an override): the original share
 *   runs, after a treat that settles at once as `skipped`.
 * - `variant_1` and `variant_2`: `copyAtTap` copies the post inside the
 *   tap. Then the caller's `onBeforeAction` closes the slide-up, and `onSheetTap` fires the tap events
 *   and opens the sheet. No composer opens at the tap.
 *
 * The wrapper component's identity does not depend on `params`, which are read at tap time through a
 * ref. A new component type would remount the Facebook buttons whenever the fetch resolves, and drop a
 * tap whose pointerdown and pointerup straddle the swap.
 */
export function useFacebookPostSheetInterception<T extends readonly InterceptableShareConfig[]>(
	buttonConfigs: T,
	params: Params,
): ReadonlyArray<T[number]> {
	const paramsRef = useRef(params);
	useEffect(() => {
		paramsRef.current = params;
	}, [params]);

	return useMemo(() => {
		const facebookIndex = buttonConfigs.findIndex((config) => config.channel === 'facebook');
		if (facebookIndex === -1) return buttonConfigs;

		const original = buttonConfigs[facebookIndex];
		const OriginalShareAction = original.ShareAction;

		// A tap in progress, on either surface (grid icon or CTA), for the duration of the treat wait. The
		// slide-up stays open during that wait, so a second tap would otherwise start a second chain and
		// fire the share events, or open the composer, twice. Released once the treat settles.
		let tapInFlight = false;

		/** Treat, capped at the budget, then the probe with the outcome. Shared by both paths. */
		const treatAndReport = async () => {
			try {
				const { treatAtTap, reportTap } = paramsRef.current;
				reportTap(await treatAtTap());
			} finally {
				tapInFlight = false;
			}
		};

		const openSheet = async (
			onBeforeAction: ShareActionComponentProps['onBeforeAction'],
			trackingData: ShareTrackingDataOverride | undefined,
			copying: Promise<boolean> | undefined,
		) => {
			await treatAndReport();
			await onBeforeAction?.();
			paramsRef.current.onSheetTap({ trackingData, copied: await copying });
		};

		/** The production share for a tap that treats: the composer opens now, and the events wait. */
		const shareAtTap = async (
			{ trackingData, onAfterAction }: Omit<ShareActionComponentProps, 'children'>,
			treated: Promise<void>,
		) => {
			// Called before any await, so the composer opens inside the tap.
			const opened = await paramsRef.current.shareAtTap({ trackingData }, treated);
			await onAfterAction?.(opened);
		};

		const InterceptedShareAction = ({ onBeforeAction, children, ...props }: ShareActionComponentProps) => (
			<OriginalShareAction
				{...props}
				onBeforeAction={async () => {
					await treatAndReport();
					return onBeforeAction?.();
				}}
			>
				<TapRouter
					child={children}
					decide={(productionShare) => {
						if (tapInFlight) return;
						paramsRef.current.onTap();
						if (paramsRef.current.sheetArm) {
							tapInFlight = true;
							// Inside the tap, before any await: iOS Safari blocks a clipboard write after the tap ends,
							// and the sheet opens only after the treat wait.
							const copying = paramsRef.current.copyAtTap();
							void openSheet(onBeforeAction, props.trackingData, copying);
						} else if (paramsRef.current.treatsAtTap) {
							tapInFlight = true;
							const treated = treatAndReport();
							void onBeforeAction?.();
							void shareAtTap(props, treated);
						} else if (productionShare) {
							// Released by `treatAndReport`, which the production share's `onBeforeAction` runs.
							tapInFlight = true;
							productionShare();
						}
					}}
				/>
			</OriginalShareAction>
		);

		const intercepted = [...buttonConfigs];
		intercepted[facebookIndex] = { ...original, ShareAction: InterceptedShareAction };
		return intercepted;
	}, [buttonConfigs]);
}
